import { useTheme } from "@/contexts/ThemeContext";
import { useBalance } from "@/contexts/BalanceContext";
import { motion } from "framer-motion";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState, useEffect } from "react";
import { CurrencyBadge, GCBadgeSVG, SCBadgeSVG } from "@/components/CurrencyBadge";

export const CurrencySlider = () => {
  const { selectedCurrency, setSelectedCurrency, formatCurrency } = useTheme();
  const { balance } = useBalance();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Auto-close popover after 3 seconds
  useEffect(() => {
    if (isPopoverOpen) {
      const timer = setTimeout(() => {
        setIsPopoverOpen(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isPopoverOpen]);

  const getTotalSC = () => {
    if (!balance) return 0;
    const safeNumber = (val: unknown): number => {
      const num = Number(val);
      return typeof num === 'number' && !isNaN(num) ? num : 0;
    };
    
    const scBonus = safeNumber(balance.sc_bonus);
    const scPurchased = safeNumber(balance.sc_purchased);
    const scRedeemable = safeNumber(balance.sc_redeemable);
    return scBonus + scPurchased + scRedeemable;
  };

  const handleCurrencyClick = (currency: 'GC' | 'SC', e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedCurrency(currency);
    setIsPopoverOpen(true);
  };

  const gcBalance = balance?.gc_balance ?? 0;
  const totalSC = getTotalSC();
  const isGC = selectedCurrency === 'GC';

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <div className="relative flex items-center cursor-pointer select-none">
          {/* Outer container - frosted glass */}
          <div className="relative flex items-center rounded-xl p-1 bg-muted/60 backdrop-blur-md border border-border shadow-lg shadow-black/10">
            
            {/* Sliding indicator */}
            <motion.div
              className={`absolute top-1 bottom-1 rounded-[10px] shadow-md ${
                isGC 
                  ? 'bg-gradient-to-b from-amber-400/90 to-amber-600/90 shadow-amber-500/20' 
                  : 'bg-gradient-to-b from-emerald-400/90 to-emerald-600/90 shadow-emerald-500/20'
              }`}
              initial={false}
              animate={{
                left: isGC ? '4px' : 'calc(50%)',
                width: 'calc(50% - 4px)',
              }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            />

            {/* GC Section */}
            <button
              onClick={(e) => handleCurrencyClick('GC', e)}
              className="relative z-10 flex items-center gap-2.5 px-3 py-1.5 min-w-[160px] sm:min-w-[190px]"
            >
              <GCBadgeSVG size={30} />
              <span className={`text-sm sm:text-base font-semibold transition-colors duration-200 tabular-nums ${
                isGC ? 'text-foreground' : 'text-muted-foreground/50'
              }`}>
                {formatCurrency(gcBalance, 'GC')}
              </span>
            </button>

            {/* SC Section */}
            <button
              onClick={(e) => handleCurrencyClick('SC', e)}
              className="relative z-10 flex items-center gap-2.5 px-3 py-1.5 min-w-[160px] sm:min-w-[190px]"
            >
              <SCBadgeSVG size={30} />
              <span className={`text-sm sm:text-base font-semibold transition-colors duration-200 tabular-nums ${
                !isGC ? 'text-foreground' : 'text-muted-foreground/50'
              }`}>
                {formatCurrency(totalSC, 'SC')}
              </span>
            </button>
          </div>
        </div>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-72 p-0 bg-popover/95 backdrop-blur-xl border border-border shadow-2xl rounded-xl overflow-hidden"
        sideOffset={8}
      >
        {/* Header with gradient */}
        <div className={`p-4 ${
          isGC 
            ? 'bg-gradient-to-r from-amber-500/20 to-transparent' 
            : 'bg-gradient-to-r from-emerald-500/20 to-transparent'
        }`}>
          <div className="flex items-center gap-3">
            <CurrencyBadge type={selectedCurrency} size="lg" />
            <div>
              <h3 className="font-bold text-popover-foreground text-sm">
                {isGC ? 'Gold Coins' : 'Sweep Coins'}
              </h3>
              <p className={`text-xs font-medium ${isGC ? 'text-amber-400' : 'text-emerald-400'}`}>
                Currently Active
              </p>
            </div>
          </div>
        </div>
        
        {/* Description */}
        <div className="px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground leading-relaxed">
            {isGC 
              ? 'Gold Coins are for fun play only. Play anytime without restrictions.'
              : 'Sweep Coins can win real prizes. Winnings are redeemable at 1 SC = $1 USD.'
            }
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};
